import type React from 'react';
import { useState, useEffect } from 'react';

import { useIntl, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import FilterList from '@mui/icons-material/FilterList';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { green, blueGrey, red, deepOrange } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Spacings from '~/modules/ui-kit/spacings';
import { usePublisherSearch } from '~/hooks/api/usePublisherSearch';
import LoadingView from '~/components/LoadingView';
import PublisherFilter from '~/components/PublisherFilter';
import Tag from '~/components/Tag';
import commonMessages from '~/containers/App/messages';
import { useTheme } from '@mui/material';
import { useInvitePublishers } from '~/hooks/api/useInvitePublishers';
import styles from './styles.module.css';
import messages from './messages';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

const tagColors = {
  active: {
    backgroundColor: green[50],
    color: green[900],
  },
  locked: {
    backgroundColor: red[50],
    color: red[900],
  },
  inactive: {
    backgroundColor: blueGrey[50],
    color: blueGrey[900],
  },
  invited: {
    backgroundColor: deepOrange[50],
    color: deepOrange[900],
  },
  atrisktobecomeinactive: {
    backgroundColor: blueGrey[50],
    color: blueGrey[900],
  },
};

type SearchableUserTableProps = {
  query: string;
  filter: string[];
  onEditUser: (userId: number) => void;
  initialPage: number;
  onChangePage: (newPage: number) => void;
};

const SearchableUserTable = (props: SearchableUserTableProps) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedPublisherIds, setSelectedPublisherIds] = useState<number[]>(
    []
  );
  const [selectedPublisherEmails, setSelectedPublisherEmails] = useState<
    string[]
  >([]);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [page, setPage] = useState(props.initialPage || 0);
  const [perPage, setPerPage] = useState(20);
  const mobileAndAbove = useMediaQuery('(min-width: 36em)');
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const { query, onEditUser, onChangePage, initialPage, filter } = props;
  const { formatMessage } = intl;

  const invitePublishersMutation = useInvitePublishers({
    onSuccess: () => {
      showSnackbar({
        message: formatMessage(messages.invitePublishersSucceeded),
      });
    },
    onError: () => {
      showSnackbar({
        message: formatMessage(messages.invitePublishersFailed),
      });
    },
  });

  // Sync page state with initialPage prop
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(initialPage ?? 0);
  }, [initialPage]);

  // Paginated query for table
  const { data: publishers, isLoading } = usePublisherSearch(
    query,
    undefined,
    filter,
    ['lastname'],
    page,
    perPage
  );

  // Query for all publishers (for email button)
  const { data: allPublishers, isLoading: isLoadingAll } = usePublisherSearch(
    query,
    undefined,
    filter,
    ['lastname'],
    undefined,
    9999
  );

  const handleCloseActionMenu = () => {
    setActionMenuAnchorEl(null);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    if (onChangePage) {
      onChangePage(newPage);
    }
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <div>
      <Spacings.Inset>
        <Dialog
          fullScreen={!mobileAndAbove}
          open={filterDialogOpen}
          aria-labelledby="filter-dialog-title"
          onClose={() => setFilterDialogOpen(false)}
        >
          <DialogTitle id="filter-dialog-title">
            <FormattedMessage {...messages.filterSettings} />
          </DialogTitle>
          <DialogContent>
            <PublisherFilter />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)} color="primary">
              <FormattedMessage {...messages.apply} />
            </Button>
          </DialogActions>
        </Dialog>
        {selectedPublisherIds.length > 0 ? (
          <Button
            aria-controls="publisher-action-menu"
            aria-haspopup="true"
            onClick={(event) => setActionMenuAnchorEl(event.currentTarget)}
            variant="outlined"
          >
            <FormattedMessage
              {...messages.actionForSelected}
              values={{
                numberOfSelectedPublishers: selectedPublisherIds.length,
              }}
            />
          </Button>
        ) : (
          <Spacings.Inline className={styles.actions} alignItems="center">
            {!isLoadingAll && (
              <Typography color="textSecondary">
                <FormattedMessage
                  {...messages.numberOfResults}
                  values={{ numberOfResults: allPublishers?.length || 0 }}
                />
              </Typography>
            )}
            <div className={styles.right}>
              <Button
                variant="outlined"
                href={`mailto:?bcc=${(allPublishers || [])
                  .map((publisher) => encodeURIComponent(publisher.email))
                  .join(',')}`}
                disabled={isLoadingAll}
                startIcon={<SendIcon />}
              >
                {isLoadingAll ? (
                  <FormattedMessage {...commonMessages.loading} />
                ) : (
                  <FormattedMessage {...messages.sendEmailToAll} />
                )}
              </Button>
            </div>
            <div>
              <Button
                onClick={() => setFilterDialogOpen(true)}
                variant="outlined"
                startIcon={<FilterList />}
              >
                <FormattedMessage {...messages.filter} />
              </Button>
            </div>
          </Spacings.Inline>
        )}
        <Menu
          id="publisher-action-menu"
          anchorEl={actionMenuAnchorEl}
          keepMounted
          open={Boolean(actionMenuAnchorEl)}
          onClose={handleCloseActionMenu}
        >
          <MenuItem
            onClick={async () => {
              await invitePublishersMutation.mutateAsync({
                publisherIds: selectedPublisherIds,
              });
              handleCloseActionMenu();
            }}
            disabled={invitePublishersMutation.isPending}
          >
            {invitePublishersMutation.isPending ? (
              <FormattedMessage {...commonMessages.loading} />
            ) : (
              <FormattedMessage {...messages.inviteSelectedPublishers} />
            )}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseActionMenu();
            }}
          >
            <a
              href={`mailto:?bcc=${selectedPublisherEmails
                .map((publisherEmail) => encodeURIComponent(publisherEmail))
                .join(',')}`}
            >
              <FormattedMessage {...messages.sendEmailToSelectedPublishers} />
            </a>
          </MenuItem>
        </Menu>
      </Spacings.Inset>
      <div
        className={classnames({
          [styles['loading-wrapper']]: isLoading,
        })}
      >
        {isLoading ? (
          <LoadingView hideLabel />
        ) : (
          <>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedPublisherIds.length > 0 &&
                        selectedPublisherIds.length < (publishers?.length || 0)
                      }
                      checked={
                        publishers &&
                        selectedPublisherIds.length === publishers.length &&
                        publishers.length > 0
                      }
                      onChange={() => {
                        if (selectedPublisherIds.length > 0) {
                          setSelectedPublisherIds([]);
                          setSelectedPublisherEmails([]);
                        } else {
                          setSelectedPublisherIds(
                            (publishers || []).map((publisher) => publisher.id)
                          );
                          setSelectedPublisherEmails(
                            (publishers || []).map(
                              (publisher) => publisher.email
                            )
                          );
                        }
                      }}
                      slotProps={{
                        input: {
                          'aria-label': formatMessage(
                            messages.selectAllPublishers
                          ),
                        },
                      }}
                    />
                  </TableCell>
                  {isDesktop ? (
                    <>
                      <TableCell>{formatMessage(messages.firstName)}</TableCell>
                      <TableCell>{formatMessage(messages.lastName)}</TableCell>
                      <TableCell>{formatMessage(messages.email)}</TableCell>
                    </>
                  ) : (
                    <TableCell>{formatMessage(messages.publisher)}</TableCell>
                  )}
                  <TableCell>{formatMessage(messages.status)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {publishers && publishers.length > 0 ? (
                  publishers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPublisherIds.includes(user.id)}
                          onChange={() => {
                            if (selectedPublisherIds.includes(user.id)) {
                              setSelectedPublisherIds(
                                selectedPublisherIds.filter(
                                  (publisherId) => publisherId !== user.id
                                )
                              );
                              setSelectedPublisherEmails(
                                selectedPublisherEmails.filter(
                                  (publisherEmail) =>
                                    publisherEmail !== user.email
                                )
                              );
                            } else {
                              setSelectedPublisherIds([
                                ...selectedPublisherIds,
                                user.id,
                              ]);
                              setSelectedPublisherEmails([
                                ...selectedPublisherEmails,
                                user.email,
                              ]);
                            }
                          }}
                          slotProps={{
                            input: {
                              'aria-label': formatMessage(
                                messages.selectPublisher
                              ),
                            },
                          }}
                        />
                      </TableCell>
                      {isDesktop ? (
                        <>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                        </>
                      ) : (
                        <TableCell>
                          <div>
                            {user.firstName} {user.lastName}
                          </div>
                          <Typography variant="body2" color="textSecondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Tag
                          style={
                            tagColors[
                              user.state?.toLowerCase() as keyof typeof tagColors
                            ]
                          }
                        >
                          {messages[
                            `state${user.state}` as keyof typeof messages
                          ]
                            ? formatMessage(
                                messages[
                                  `state${user.state}` as keyof typeof messages
                                ]
                              )
                            : user.state}
                        </Tag>
                      </TableCell>
                      <TableCell align="right" padding="none">
                        <IconButton
                          onClick={() => onEditUser(user.id)}
                          data-test="edit-user"
                          aria-label={formatMessage(messages.editPublisher)}
                          component="span"
                          size="large"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography
                        data-test="no-search-results"
                        color="textSecondary"
                      >
                        {formatMessage(messages.noSearchResults)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {publishers && publishers.length > 0 && (
              <Spacings.Inset
                scale="s"
                className={styles['pagination-controls']}
              >
                <TablePagination
                  component="div"
                  count={-1}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={perPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[20, 50, 100]}
                  labelDisplayedRows={() => ''}
                />
              </Spacings.Inset>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchableUserTable;
